import React, { Component } from "react";
import { BASE_URL } from "../common/common";

const API_URL = BASE_URL;
const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3 MB

const imgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return `${path}?ngrok-skip-browser-warning=true`;
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  return `${API_URL}/${cleanPath}?ngrok-skip-browser-warning=true`;
};

const emptySocialForm = () => ({
  platform     : "",
  url          : "",
  iconPreview  : null,
  iconFile     : null,
  saving       : false,
});

// ── Social platform presets ──────────────────
const SOCIAL_PLATFORMS = [
  { label: "Facebook",  icon: "f", color: "#1877f2" },
  { label: "Instagram", icon: "in", color: "#e1306c" },
  { label: "YouTube",   icon: "yt", color: "#ff0000" },
  { label: "LinkedIn",  icon: "li", color: "#0077b5" },
  { label: "Twitter/X", icon: "x",  color: "#000000" },
  { label: "Custom",    icon: "?",  color: "#888888" },
];

class Settings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab : "logo",

      // ── Logo Section
      logoData          : null,
      logoLoading       : false,
      logoSaving        : false,
      headerLogoPreview : null,
      headerLogoFile    : null,
      footerLogoPreview : null,
      footerLogoFile    : null,
      footerContent     : "",

      // ── Social Media
      socialView    : "list",
      socialList    : [],
      socialLoading : false,
      socialForm    : emptySocialForm(),
      editingSocial : null,

      successMessage : "",
      errorMessage   : "",
    };
  }

  componentDidMount() {
    this.fetchLogo();
    this.fetchSocials();
  }

  showSuccess = (msg) => { this.setState({ successMessage: msg, errorMessage: "" }); setTimeout(() => this.setState({ successMessage: "" }), 3000); };
  showError   = (msg) => { this.setState({ errorMessage: msg, successMessage: "" }); setTimeout(() => this.setState({ errorMessage: "" }), 4000); };

  // ════════════════════════════════
  //  1. LOGO SECTION
  // ════════════════════════════════

  fetchLogo = async () => {
    this.setState({ logoLoading: true });
    try {
      const res  = await fetch(`${API_URL}/api/setting/getSettings`, { method: "GET", headers: { "ngrok-skip-browser-warning": "true" } });
      const json = await res.json();
      if (json.status) {
        const data = json.data?.[0]?.[0] || json.data?.[0] || json.data || {};
        this.setState({
          logoData      : data,
          footerContent : data.footerDescription || data.footerDescription || "",
          logoLoading   : false,
        });
      } else { this.setState({ logoLoading: false }); }
    } catch { this.setState({ logoLoading: false }); this.showError("Cannot connect to API."); }
  };

  handleHeaderLogo = (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) { this.showError("Image size must be less than 3 MB."); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onloadend = () => this.setState({ headerLogoPreview: reader.result, headerLogoFile: file });
    reader.readAsDataURL(file);
  };

  handleFooterLogo = (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) { this.showError("Image size must be less than 3 MB."); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onloadend = () => this.setState({ footerLogoPreview: reader.result, footerLogoFile: file });
    reader.readAsDataURL(file);
  };

  saveLogo = async () => {
    const { headerLogoFile, footerLogoFile, footerContent, logoData } = this.state;
    this.setState({ logoSaving: true });
    try {
      const fd = new FormData();
      fd.append("footerdescription", footerContent);
      if (headerLogoFile) fd.append("headerurl", headerLogoFile);
      if (footerLogoFile) fd.append("footerurl",  footerLogoFile);
      if (logoData?.setting_id)   fd.append("setting_id", logoData.setting_id);

      const url = logoData?.setting_id
        ? `${API_URL}/api/setting/updatesettings`
        : `${API_URL}/api/setting/addSettings`;

      const res  = await fetch(url, { method: "POST", body: fd });
      const json = await res.json();
      this.setState({ logoSaving: false });
      if (json.status) {
        this.showSuccess("Logo settings saved!");
        this.setState({ headerLogoPreview: null, headerLogoFile: null, footerLogoPreview: null, footerLogoFile: null });
        this.fetchLogo();
      } else { this.showError(json.message || "Save failed."); }
    } catch { this.setState({ logoSaving: false }); this.showError("Cannot connect to API."); }
  };

  // ════════════════════════════════
  //  2. SOCIAL MEDIA
  // ════════════════════════════════

  fetchSocials = async () => {
    this.setState({ socialLoading: true });
    try {
      const res  = await fetch(`${API_URL}/api/socialmedia/getSocialMedia`, { method: "GET", headers: { "ngrok-skip-browser-warning": "true" } });
      const json = await res.json();
      if (json.status) {
        this.setState({ socialList: json.data || json.data || [], socialLoading: false });
      } else { this.setState({ socialLoading: false }); }
    } catch { this.setState({ socialLoading: false }); this.showError("Cannot connect to API."); }
  };

  openSocialForm = (item = null) => {
    this.setState({
      socialView    : "form",
      editingSocial : item,
      socialForm    : item
        ? { platform: item.name || item.name || "", url: item.pageUrl || item.pageUrl || "", iconPreview: null, iconFile: null, saving: false }
        : emptySocialForm(),
    });
  };

  goToSocialList = () => { this.setState({ socialView: "list", editingSocial: null }); this.fetchSocials(); };

  handleSocialIcon = (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) { this.showError("Image size must be less than 3 MB."); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onloadend = () => this.setState((prev) => ({ socialForm: { ...prev.socialForm, iconPreview: reader.result, iconFile: file } }));
    reader.readAsDataURL(file);
  };

  saveSocial = async () => {
    const { socialForm, editingSocial } = this.state;
    if (!socialForm.platform.trim()) return this.showError("Platform name is required.");
    if (!socialForm.url.trim())      return this.showError("URL is required.");
    this.setState((prev) => ({ socialForm: { ...prev.socialForm, saving: true } }));
    try {
      const fd = new FormData();
      fd.append("name", socialForm.platform);
      fd.append("pageUrl",      socialForm.url);
      if (socialForm.iconFile) fd.append("logoUrl", null);
      if (editingSocial) fd.append("social_media_id", editingSocial.social_media_id || editingSocial.social_media_id);
      const url = editingSocial
        ? `${API_URL}/api/socialmedia/updateSocialMedia`
        : `${API_URL}/api/socialmedia/addSocialMedia`;
      const res  = await fetch(url, { method: "POST", body: fd });
      const json = await res.json();
      this.setState((prev) => ({ socialForm: { ...prev.socialForm, saving: false } }));
      if (json.status) { this.showSuccess(editingSocial ? "Updated!" : "Saved!"); setTimeout(() => this.goToSocialList(), 1000); }
      else { this.showError(json.message || "Save failed."); }
    } catch { this.setState((prev) => ({ socialForm: { ...prev.socialForm, saving: false } })); this.showError("Cannot connect to API."); }
  };

  deleteSocial = async (idx) => {
    if (!window.confirm("Delete this social media link?")) return;
    try {
      const res  = await fetch(`${API_URL}/api/socialmedia/deleteSocialMedia`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id:idx }) });
      const json = await res.json();
      if (json.status) { this.showSuccess("Deleted."); this.fetchSocials(); }
      else { this.showError(json.message || "Delete failed."); }
    } catch { this.showError("Cannot connect to API."); }
  };

  // ════════════════════════════════
  //  RENDER
  // ════════════════════════════════
  render() {
    const {
      activeTab,
      logoData, logoLoading, logoSaving,
      headerLogoPreview, headerLogoFile, footerLogoPreview, footerLogoFile, footerContent,
      socialView, socialList, socialLoading, socialForm, editingSocial,
      successMessage, errorMessage,
    } = this.state;

    return (
      <div>
        <div className="page-header">
          <h2>Settings</h2>
          <p>Manage site logo, footer content and social media links.</p>
        </div>

        {successMessage && <div style={s.success}>{successMessage}</div>}
        {errorMessage   && <div style={s.error}>{errorMessage}</div>}

        {/* Tabs */}
        <div className="rsp-tab-row" style={s.tabRow}>
          {["logo", "social"].map((tab) => (
            <button key={tab} style={activeTab === tab ? s.tabActive : s.tab} onClick={() => this.setState({ activeTab: tab })}>
              {tab === "logo" ? "🖼 Logo & Footer" : "📱 Social Media"}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════
            1. LOGO SECTION
        ══════════════════════════════ */}
        {activeTab === "logo" && (
          <div>
            {logoLoading ? (
              <div style={s.emptyBox}><p style={s.emptyText}>Loading...</p></div>
            ) : (
              <div className="card">

                {/* Section heading */}
                <div style={s.sectionHead}>
                  <h3 style={s.sectionTitle}>Logo & Branding</h3>
                  <p style={s.sectionSub}>Update site logos and footer description text.</p>
                </div>

                {/* ── Header Logo ── */}
                <div style={s.logoRow}>
                  <div style={s.logoInfo}>
                    <label style={s.label}>Header Logo</label>
                    <p style={s.hint}>Displayed in the top navigation bar. Recommended: transparent PNG, 200×60px.</p>
                  </div>
                  <div style={s.logoUploadArea}>
                    {/* Current */}
                    {/* ✅ API field: headerlogo */}
                    {logoData && logoData.headerlogo && !headerLogoPreview && (
                      <div style={{ marginBottom: "10px" }}>
                        <p style={{ fontSize: "11px", color: "#aaa", marginBottom: "6px" }}>Current:</p>
                        <img src={imgUrl(logoData.headerlogo)} alt="header logo" style={s.logoPreviewImg} />
                      </div>
                    )}
                    {headerLogoPreview ? (
                      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                        <img src={headerLogoPreview} alt="new header logo" style={s.logoPreviewImg} />
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          <label className="rsp-btn-dark" style={s.btnDark}>Change<input type="file" accept="image/*" onChange={this.handleHeaderLogo} style={{ display: "none" }} /></label>
                          <button style={s.btnDeleteSm} onClick={() => this.setState({ headerLogoPreview: null, headerLogoFile: null })}>Remove</button>
                        </div>
                      </div>
                    ) : (
                      <label style={s.uploadBoxSm}>
                        <p style={s.uploadText}>{logoData?.headerlogo ? "Click to replace" : "Click to upload"}</p>
                        <p style={s.uploadHint}>PNG with transparency recommended</p>
                        <input type="file" accept="image/*" onChange={this.handleHeaderLogo} style={{ display: "none" }} />
                      </label>
                    )}
                  </div>
                </div>

                <div style={s.divider} />

                {/* ── Footer Logo ── */}
                <div style={s.logoRow}>
                  <div style={s.logoInfo}>
                    <label style={s.label}>Footer Logo</label>
                    <p style={s.hint}>Displayed in the footer section. Can be a white/light version of the logo.</p>
                  </div>
                  <div style={s.logoUploadArea}>
                    {/* ✅ API field: footerlogo */}
                    {logoData && logoData.footerlogo && !footerLogoPreview && (
                      <div style={{ marginBottom: "10px" }}>
                        <p style={{ fontSize: "11px", color: "#aaa", marginBottom: "6px" }}>Current:</p>
                        <div style={{ background: "#1a1a2e", padding: "10px", borderRadius: "6px", display: "inline-block" }}>
                          <img src={imgUrl(logoData.footerlogo)} alt="footer logo" style={s.logoPreviewImg} />
                        </div>
                      </div>
                    )}
                    {footerLogoPreview ? (
                      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                        <div style={{ background: "#1a1a2e", padding: "10px", borderRadius: "6px" }}>
                          <img src={footerLogoPreview} alt="new footer logo" style={s.logoPreviewImg} />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          <label className="rsp-btn-dark" style={s.btnDark}>Change<input type="file" accept="image/*" onChange={this.handleFooterLogo} style={{ display: "none" }} /></label>
                          <button style={s.btnDeleteSm} onClick={() => this.setState({ footerLogoPreview: null, footerLogoFile: null })}>Remove</button>
                        </div>
                      </div>
                    ) : (
                      <label style={s.uploadBoxSm}>
                        <p style={s.uploadText}>{logoData?.footerlogo ? "Click to replace" : "Click to upload"}</p>
                        <p style={s.uploadHint}>White/light version recommended</p>
                        <input type="file" accept="image/*" onChange={this.handleFooterLogo} style={{ display: "none" }} />
                      </label>
                    )}
                  </div>
                </div>

                <div style={s.divider} />

                {/* ── Footer Content ── */}
                <div className="rsp-form-group" style={s.formGroup}>
                  <label style={s.label}>Footer Description / Content</label>
                  <p style={s.hint}>Short company description shown below the footer logo.</p>
                  <textarea
                    style={{ ...s.textarea, minHeight: "100px" }}
                    value={footerContent}
                    placeholder="TNA Events & Sports Pvt. Ltd. is an event management company committed to delivering integrated experiential communication solutions."
                    onChange={(e) => this.setState({ footerContent: e.target.value })}
                  />
                  <span style={{ fontSize: "11px", color: "#aaa" }}>{footerContent.length} characters</span>
                </div>

                {/* Save */}
                <div style={{ display: "flex", justifyContent: "flex-end", borderTop: "1px solid #f0f0f0", paddingTop: "16px" }}>
                  <button className="rsp-btn-save" style={s.btnSave} onClick={this.saveLogo} disabled={logoSaving}>
                    {logoSaving ? "Saving..." : "Save Logo Settings"}
                  </button>
                </div>

              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════
            2. SOCIAL MEDIA
        ══════════════════════════════ */}
        {activeTab === "social" && (
          <div>

            {/* Social List */}
            {socialView === "list" && (
              <div>
                <div className="rsp-list-top" style={s.listTopBar}>
                  <span style={s.countText}>{socialLoading ? "Loading..." : `${socialList.length} social link${socialList.length !== 1 ? "s" : ""} found`}</span>
                  <button className="rsp-btn-save" style={s.btnSave} onClick={() => this.openSocialForm()}>+ Add Social Link</button>
                </div>

                {socialLoading && <div style={s.emptyBox}><p style={s.emptyText}>Loading...</p></div>}
                {!socialLoading && socialList.length === 0 && (
                  <div style={s.emptyBox}>
                    <p style={{ ...s.emptyText, marginBottom: "12px" }}>No social media links found.</p>
                    <button className="rsp-btn-save" style={s.btnSave} onClick={() => this.openSocialForm()}>+ Add First Link</button>
                  </div>
                )}

                {!socialLoading && socialList.map((item, i) => {
                  const platform = item.name || item.name || "Social";
                  const url      = item.pageUrl      || item.pageUrl || "";
                  const iconUrl  = item.icon     || item.iconUrl    || item.logo;
                  const preset   = SOCIAL_PLATFORMS.find(p => platform.toLowerCase().includes(p.label.toLowerCase().split("/")[0]));
                  return (
                    <div key={item.social_media_id || item.social_media_id || i} className="rsp-list-card" style={s.listCard}>

                      {/* Icon */}
                      {iconUrl ? (
                        <img src={imgUrl(iconUrl)} alt={platform} style={s.socialIcon} />
                      ) : (
                        <div style={{ ...s.socialIconPlaceholder, background: preset?.color || "#888" }}>
                          {(preset?.icon || platform.charAt(0)).toUpperCase()}
                        </div>
                      )}

                      {/* Details */}
                      <div style={{ flex: 1 }}>
                        <div style={s.cardTitle}>
                          {platform}
                          <span style={s.idBadge}>ID: {item.social_media_id || item.social_media_id}</span>
                        </div>
                        <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: "12px", color: "#1565c0", wordBreak: "break-all" }}>
                          {url}
                        </a>
                      </div>

                      {/* Edit / Delete */}
                      <div className="rsp-action-btns" style={s.actionBtns}>
                        <button className="rsp-btn-edit" style={s.btnEdit}   onClick={() => this.openSocialForm(item)}>Edit</button>
                        <button className="rsp-btn-delete" style={s.btnDelete} onClick={() => this.deleteSocial(item.social_media_id || item.social_media_id)}>Delete</button>
                      </div>

                    </div>
                  );
                })}
              </div>
            )}

            {/* Social Form */}
            {socialView === "form" && (
              <div>
                <div className="rsp-form-top" style={s.formTopBar}>
                  <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToSocialList}>Back to List</button>
                  <h3 style={s.formTitle}>{editingSocial ? `Edit — ${editingSocial.platform || editingSocial.name}` : "Add Social Media Link"}</h3>
                </div>

                <div className="card">

                  {/* Platform selector */}
                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Platform *</label>
                   
                    <input
                      className="rsp-input" style={s.input}
                      type="text"
                      value={socialForm.platform}
                      placeholder="Platform name (e.g. Facebook, Instagram)"
                      onChange={(e) => this.setState((prev) => ({ socialForm: { ...prev.socialForm, platform: e.target.value } }))}
                    />
                  </div>

                  {/* URL */}
                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Profile URL *</label>
                    <input
                      className="rsp-input" style={s.input}
                      type="text"
                      value={socialForm.url}
                      placeholder="https://www.facebook.com/tnaevents"
                      onChange={(e) => this.setState((prev) => ({ socialForm: { ...prev.socialForm, url: e.target.value } }))}
                    />
                  </div>

                  {/* Custom Icon Upload */}
                  {/* <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Custom Icon <span style={{ color: "#aaa", fontWeight: "400" }}>(optional — uses platform color if not set)</span></label>
                    {editingSocial && !socialForm.iconPreview && (editingSocial.icon || editingSocial.iconUrl || editingSocial.logo) && (
                      <div style={{ marginBottom: "10px" }}>
                        <p style={s.hint}>Current icon:</p>
                        <img src={imgUrl(editingSocial.icon || editingSocial.iconUrl || editingSocial.logo)} alt="icon" style={{ width: "48px", height: "48px", objectFit: "contain", borderRadius: "8px", border: "1px solid #e8e8e8" }} />
                      </div>
                    )}
                    {socialForm.iconPreview ? (
                      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                        <img src={socialForm.iconPreview} alt="icon preview" style={{ width: "56px", height: "56px", objectFit: "contain", borderRadius: "8px", border: "1px solid #e8e8e8" }} />
                        <div style={{ display: "flex", gap: "8px" }}>
                          <label className="rsp-btn-dark" style={s.btnDark}>Change<input type="file" accept="image/*" onChange={this.handleSocialIcon} style={{ display: "none" }} /></label>
                          <button style={s.btnDeleteSm} onClick={() => this.setState((p) => ({ socialForm: { ...p.socialForm, iconPreview: null, iconFile: null } }))}>Remove</button>
                        </div>
                      </div>
                    ) : (
                      <label style={{ ...s.uploadBoxSm, maxWidth: "300px" }}>
                        <p style={s.uploadText}>Click to upload icon</p>
                        <p style={s.uploadHint}>PNG/SVG — square icon (48×48px)</p>
                        <input type="file" accept="image/*" onChange={this.handleSocialIcon} style={{ display: "none" }} />
                      </label>
                    )}
                  </div> */}

                  <div className="rsp-form-actions" style={s.formActions}>
                    <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToSocialList}>Cancel</button>
                    <button className="rsp-btn-save" style={s.btnSave} onClick={this.saveSocial} disabled={socialForm.saving}>
                      {socialForm.saving ? "Saving..." : editingSocial ? `Update ${socialForm.platform || "Link"}` : `Save ${socialForm.platform || "Link"}`}
                    </button>
                  </div>

                </div>
              </div>
            )}
          </div>
        )}

      </div>
    );
  }
}

const s = {
  success          : { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "14px" },
  error            : { background: "#ffebee", color: "#c62828", border: "1px solid #ffcdd2", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "14px" },
  tabRow           : { display: "flex", gap: "10px", borderBottom: "2px solid #e8e8e8", marginBottom: "20px" },
  tab              : { padding: "10px 20px", background: "none", border: "none", borderBottom: "3px solid transparent", fontSize: "14px", color: "#888", cursor: "pointer", marginBottom: "-2px", fontWeight: "500" },
  tabActive        : { padding: "10px 20px", background: "none", border: "none", borderBottom: "3px solid #e8a020", fontSize: "14px", color: "#e8a020", cursor: "pointer", marginBottom: "-2px", fontWeight: "600" },
  // Logo section
  sectionHead      : { marginBottom: "24px", paddingBottom: "16px", borderBottom: "1px solid #f0f0f0" },
  sectionTitle     : { fontSize: "16px", color: "#1a1a2e", fontWeight: "700", margin: "0 0 4px" },
  sectionSub       : { fontSize: "13px", color: "#888", margin: 0 },
  logoRow          : { display: "grid", gridTemplateColumns: "280px 1fr", gap: "24px", alignItems: "start", marginBottom: "20px" },
  logoInfo         : { },
  logoUploadArea   : { },
  logoPreviewImg   : { maxWidth: "180px", maxHeight: "70px", objectFit: "contain", display: "block" },
  uploadBoxSm      : { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "2px dashed #ddd", borderRadius: "10px", padding: "20px 16px", cursor: "pointer", background: "#fafafa", textAlign: "center" },
  divider          : { borderTop: "1px solid #f0f0f0", margin: "20px 0" },
  // Social list
  listTopBar       : { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  countText        : { fontSize: "14px", color: "#888" },
  listCard         : { background: "white", borderRadius: "10px", padding: "16px 18px", border: "1px solid #e8e8e8", marginBottom: "12px", display: "flex", alignItems: "center", gap: "14px" },
  cardTitle        : { fontSize: "15px", color: "#1a1a2e", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" },
  idBadge          : { background: "#f0f2f5", color: "#888", border: "1px solid #e0e0e0", borderRadius: "10px", padding: "2px 8px", fontSize: "11px", fontWeight: "400" },
  socialIcon       : { width: "44px", height: "44px", objectFit: "contain", borderRadius: "8px", border: "1px solid #e8e8e8", flexShrink: 0 },
  socialIconPlaceholder: { width: "44px", height: "44px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "16px", fontWeight: "800", flexShrink: 0 },
  actionBtns       : { display: "flex", flexDirection: "column", gap: "6px", flexShrink: 0 },
  btnEdit          : { background: "#e3f2fd", color: "#1565c0", border: "1px solid #bbdefb", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600", minWidth: "64px" },
  btnDelete        : { background: "#ffebee", color: "#c62828", border: "1px solid #ffcdd2", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600", minWidth: "64px" },
  btnDeleteSm      : { background: "#ffebee", color: "#c62828", border: "1px solid #ffcdd2", padding: "5px 12px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600" },
  emptyBox         : { background: "white", borderRadius: "10px", padding: "48px 20px", border: "1px solid #e8e8e8", textAlign: "center" },
  emptyText        : { color: "#aaa", fontSize: "14px" },
  formTopBar       : { display: "flex", alignItems: "center", gap: "14px", marginBottom: "16px" },
  formTitle        : { fontSize: "16px", color: "#1a1a2e", margin: 0 },
  formActions      : { display: "flex", justifyContent: "flex-end", gap: "10px", borderTop: "1px solid #f0f0f0", paddingTop: "16px" },
  formGroup        : { marginBottom: "20px" },
  label            : { display: "block", fontSize: "13px", color: "#555", fontWeight: "600", marginBottom: "6px" },
  input            : { width: "100%", padding: "10px 14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", outline: "none", fontFamily: "Arial, sans-serif", boxSizing: "border-box" },
  textarea         : { width: "100%", padding: "10px 14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", outline: "none", resize: "vertical", fontFamily: "Arial, sans-serif", boxSizing: "border-box" },
  uploadText       : { fontSize: "14px", color: "#555", marginBottom: "4px" },
  uploadHint       : { fontSize: "11px", color: "#aaa" },
  hint             : { fontSize: "12px", color: "#888", marginTop: "4px", marginBottom: "6px", display: "block" },
  btnDark          : { background: "#1a1a2e", color: "#fff", border: "none", padding: "7px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600" },
  btnSave          : { background: "#e8a020", color: "#fff", border: "none", padding: "10px 20px", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" },
  btnBack          : { background: "#f0f2f5", color: "#333", border: "1px solid #ddd", padding: "9px 16px", borderRadius: "8px", fontSize: "13px", cursor: "pointer", fontWeight: "500" },
};

export default Settings;